import type { Article } from './types'

/**
 * Cross-cutting help: getting started, what's new, and reference.
 *
 * Every label quoted in bold is copied from the screen it describes. When a
 * screen's wording changes, change the article in the same commit. Screen-by-
 * screen detail lives in the field, office and certifications article files;
 * these articles link to it rather than repeat it.
 */
export const GENERAL_ARTICLES: Article[] = [
  // ------------------------------------------------------------- welcome
  {
    slug: 'welcome',
    title: 'Welcome — start here',
    section: 'Getting started',
    audience: 'all',
    summary: 'What the portal does, who can do what, and how to get help on any screen.',
    body: `The NW Linings Job Management portal is where crew time starts. Supervisors record each crew day on a phone. The office checks it and gets it ready for Sage payroll. It works alongside Sage 100. It does not replace it.

## What it does

- **Crew time.** A supervisor records the job, the date, the crew and their hours. Submitting a crew day records it and approves it. There is no separate approval step.
- **Per diem.** Per diem is ticked with the crew day, or on its own for days the crew is away but not working. The amount comes from the rate table. Nobody types it in the field.
- **Office review.** The office sees every crew day, corrects what needs correcting, applies overtime, and works the **Problems** list.
- **Sage.** The portal reads jobs, employees and paid weeks from Sage. Sending time into Sage payroll is the last step and is not switched on yet. See [Sending time to Sage](/help/sending-time-to-sage).
- **Certifications.** A separate module for training cards and who needs what on which job. It is switched on for some accounts while it is tested. See [Certifications](/help/certs-overview).

## Two modules, one switch

If you can use both Job Management and Certifications, the name beside **NW Linings** in the header is a switch: tap **Job Management** or **Certifications** to change. If you only have one of them, the header just shows its name.

## Who can do what

| Role | Signs in with | What they can do |
| --- | --- | --- |
| **Technician** | **Field** tab: employee number and PIN | See their own certifications (**Mine**), once Certifications is switched on for them. Technicians do not record time. Their supervisor does. |
| **Supervisor** | **Field** tab: employee number and PIN | **Today**, **New crew day**, **Per diem**, **My time**, **My crew**. Record days for their own crew, people on nobody's crew, and new hires. Fix their own days, with a note. In Certifications: **My crew** and **Mine**. |
| **Office** | **Office** tab: email and password | All the office screens: submissions, labor register, review by week, per diem, overtime rules, jobs, job locations and **Problems**. In Certifications: the **Employees** list, recording and verifying cards, and requirements. |
| **Admin** | **Office** tab: email and password | Everything Office can do, plus names, roles and PINs, crews, work types, wage rates, job assignments and linking new people. Can also record crew time (**Record crew time →** in the header). In Certifications: types, medical details and the alert switches. |

If you open a screen your role does not cover, you see **Not available for your role.** That is expected. Nothing is broken.

## What to try first

- **Supervisors:** follow [First week for supervisors](/help/first-week-supervisor). It takes about 20 minutes.
- **Office:** follow [First week in the office](/help/first-week-office).
- **Everyone:** read [Signing in](/help/signing-in) to put the portal on your phone's home screen.

## Getting help

- Tap the **?** button in the header. It opens help for the page you are on. On a keyboard, press the **?** key.
- At the bottom of the help panel, **All help topics** opens the full list of articles, with search. **Open in Help** opens the article you are reading on a page of its own.
- Also at the bottom of the help panel, **Send feedback** sends a problem, a question or an idea to the people running the portal. It records which page you were on, so you do not need to explain where you were. Pick **Something’s wrong**, **Question** or **Idea**, type a line or two, and tap **Send**. Any reply shows under **Your feedback** on the Help page.
- Words you do not recognize are in the [Glossary](/help/glossary). Common questions are in the [FAQ](/help/faq). If something looks wrong, try [Troubleshooting](/help/troubleshooting).`,
  },

  // ---------------------------------------------------------- signing in
  {
    slug: 'signing-in',
    title: 'Signing in and putting the portal on your phone',
    section: 'Getting started',
    audience: 'all',
    summary: 'Field and Office sign-in, staying signed in, signing out, and a home screen icon.',
    routes: ['/login'],
    body: `Go to **nwl.dash-sage.com** in your phone's or computer's web browser. There is nothing to download.

## Field sign-in (supervisors and technicians)

1. Tap the **Field** tab. It is picked already.
2. Type your **Employee number**, for example F1505.
3. Type your **PIN**. The number pad comes up.
4. Tap **Sign in**.

Your PIN is set by the office. If you forget it, ask the office to set a new one. A PIN cannot be looked up.

## Office sign-in

1. Tap the **Office** tab.
2. Type your **Email** and **Password**.
3. Click **Sign in**.

Use the password that was set up for you. Signing in with your Microsoft account is planned but not here yet.

## If sign-in fails

- **Invalid employee number or PIN** or **Invalid email or password**: check what you typed and try again.
- **Account locked. Try again shortly.**: too many wrong tries in a row. Wait about 15 minutes, or ask the office to set a new PIN, which also unlocks you.
- **Could not sign in. Check your connection.**: the phone could not reach the portal. Find signal and try again.

## Staying signed in

You stay signed in on that phone or computer, even if you close the browser or restart the phone. If you have not used the portal for about a month, you will be asked to sign in again.

Do not use a private or incognito browser window. It forgets your sign-in and any unfinished crew day when you close it.

## Signing out

Tap **Sign out** at the top right. Do this on a shared phone or computer. On your own phone you do not need to.

Send any unfinished crew day before you sign out, so nothing is left sitting on the phone.

## Put the portal on your home screen

This adds an icon that opens the portal in one tap. It is a shortcut to the website, not a separate app.

**iPhone (Safari)**

1. Open **nwl.dash-sage.com** in Safari and sign in.
2. Tap the **Share** button (the square with an arrow pointing up).
3. Scroll down and tap **Add to Home Screen**.
4. Tap **Add**.

**Android (Chrome)**

1. Open **nwl.dash-sage.com** in Chrome and sign in.
2. Tap the three-dot menu at the top right.
3. Tap **Add to Home screen**. On some phones it says **Install app**.
4. Tap **Add**.`,
  },

  // ------------------------------------------------ first week: supervisor
  {
    slug: 'first-week-supervisor',
    title: 'First week for supervisors: a walkthrough',
    section: 'Getting started',
    audience: 'field',
    summary: 'A 20 to 30 minute tour: record a crew day, check it, fix something, and record per diem.',
    body: `This tour takes 20 to 30 minutes. Do it with a real day your crew worked this week, so the numbers mean something. Have your phone and your crew list handy.

## 1. Record a crew day (10 minutes)

1. On **Today**, tap **Start crew day**.
2. Under **Job**, find and pick the job.
3. Check the **Date**.
4. If you are at the job, tap **I'm on site**. It records where your phone is. If you skip it, you will be asked why when you submit. Any reason is fine, like "at the hotel".
5. Under **Your crew**, tick the people who worked. **Same crew as last time** saves ticking everyone on the next day at the same job.
6. Tap **Next — enter hours**.
7. Use **Apply to the whole crew** to give everyone the same hours, then change anyone who was different.
8. Add travel with **+ Add travel or other** if anyone drove.
9. Tick per diem for anyone who gets it.
10. Tap **Submit crew day**. Back on **Today**, a blue box says **Sent — ...**.

All hours go in at straight time. The office works out overtime.

More detail: [Record a crew day](/help/crew-day).

## 2. Check it on My time (3 minutes)

1. Tap **My time**.
2. Find the day you just sent. Tap **Review the week** to see the whole week for that job, person by person.

More detail: [My time](/help/my-time).

## 3. Fix something on My crew (5 minutes)

1. Tap **My crew**. It shows your crew's whole week.
2. Tap a day total to see the lines behind it. Change an hour if one is wrong.
3. You will be asked for a note. A word is enough, like "typo". Every change a supervisor makes needs one.
4. You can also untick per diem here if it was ticked by mistake.

A day is locked once it has gone to Sage or Sage has paid that week. The office fixes those.

If you picked the wrong job or date, open the crew day and use **Wrong job or date?**. See [Move a crew day](/help/move-a-crew-day).

More detail: [My crew](/help/my-crew).

## 4. Per diem for a day nobody worked (3 minutes)

For a weekend the crew stays at the job instead of driving home:

1. On **Today**, tap **Per diem only**.
2. Pick the job, the days (**Last week** or **This week**) and the people.
3. Check the count above the button, for example 2 calendar days × 6 crew members = 12 per diems.
4. Tap the **Record** button.

You cannot change the amount. It comes from the rate table for the job's location.

More detail: [Per diem only](/help/per-diem-only).

## 5. Try it with no signal (optional, 2 minutes)

Put the phone in airplane mode and start a crew day. Everything you type is kept on the phone. Turn signal back on and tap **Submit crew day** again. It does not send by itself. See [Working without signal](/help/working-offline).

## 6. Tell us what you think

Use **Send feedback** at the bottom of the help panel. Useful things to send:

- Anything that took more taps than it should.
- Any word on a screen you did not understand.
- Anything the portal refused, and what you were trying to do.
- Anything your paper time sheet has that the portal does not.

It records the page you were on, so just say what happened.`,
  },

  // ---------------------------------------------------- first week: office
  // TODO(verify): no "send to Sage" action exists yet (PROJECT_STATE §5: the
  // Visual Integrator import is not built). Step 6 says so; update it, and the
  // sending-time-to-sage article, when export lands.
  {
    slug: 'first-week-office',
    title: 'First week in the office: a walkthrough',
    section: 'Getting started',
    audience: 'office',
    summary: 'A guided tour of the office screens, in the order you would use them each week.',
    body: `This tour follows a pay week from crew day to payroll. Take an hour with it, using this week's days. Last week may already be paid in Sage, and paid days are locked, so they will not let you change anything.

The office bar groups screens into dropdowns: **Time**, **Per diem**, **Pay setup**, **People** and **Jobs**. **Problems** sits on its own so its count is always in view.

## 1. Submissions

**Time → Submissions** lists every crew day as it arrives: date, job, who submitted it, crew size, lines and hours. Click one to open it, see its lines and the correction history, and fix a line.

More: [Submissions](/help/submissions).

## 2. Labor register

**Time → Labor register** shows recorded time in the same layout as Sage Time Track Entry, so you can read the two side by side.

- Group it **By employee**, **By job**, **Pay period** or **By crew day**.
- Tick **Not yet sent to Sage** to see only what is still open.
- Correct anything open directly in the grid.
- A red box at the top lists weeks that still need overtime applied. Every field hour comes in at straight time, so this is where overtime gets added.

More: [Labor register](/help/labor-register).

## 3. Review by week

**Time → Review by week** shows one job for one week, with **Hours by person and day**. It is the quickest way to spot someone with too many hours, or a day nobody recorded.

More: [Review by week](/help/review-by-week).

## 4. Problems

**Problems** is a list of things that will cause trouble at payroll time: weeks that look like overtime with none applied, prevailing wage jobs with no rate, jobs with no state, crew days moved onto a different kind of job, and more. The number on the button is how many are open.

- Fix the cause and click **Recheck**. The row goes away by itself.
- If a row is not a real problem, click **This is fine** and give a reason. **Restore** brings it back.

More: [Problems](/help/problems).

## 5. Per diem register

**Per diem → Per diem register** lays per diem out like the per diem workbook: crew blocks, one row per person, a column per day. Pick **A week** or **A month**. Someone ticked for seven days beside crewmates on two stands out right away. You can remove per diem from here.

More: [Per diem register](/help/per-diem-register).

## 6. Sending the week to Sage

Sending time from the portal into Sage payroll is not switched on yet. For now, Sage payroll is done the usual way, and the portal is checked against it. Read [Sending time to Sage](/help/sending-time-to-sage) for where this stands, and [Your weekly routine](/help/office-week-routine) for the order to work things in.

## 7. Setup screens

You will not need these every week, but look at each once:

- **People → Employees**: names (Sage hides them), roles, wage class, residence and field PINs. See [Employees](/help/employees).
- **People → New people**: new hires a supervisor added before they were in Sage. Their hours cannot go to Sage until they are linked.
- **Jobs → Jobs**: each job's per diem location. See [Jobs](/help/jobs).
- **Jobs → Job locations**: where each job is on the map, from supervisors' **I'm on site** taps. See [Job locations](/help/job-locations).
- **Pay setup**: work types, wage rates and overtime rules.
- **Certifications**: use the switch in the header. See [Certifications](/help/certs-overview).

## Tell us what you think

Use **Send feedback** at the bottom of the help panel for anything confusing, slow, or wrong. It records the page you were on.`,
  },

  // ------------------------------------------------------------ what's new
  {
    slug: 'whats-new',
    title: "What's new",
    section: 'Getting started',
    audience: 'all',
    summary: 'Recent changes you will notice, newest first.',
    body: `## October 15, 2026

- **Certifications is switched on for testing.** A small group can use it now. Everyone else sees **Certifications aren't switched on for your account.** See [Certifications](/help/certs-overview).

## October 12–13, 2026

- Certifications is its own module with its own menu. If you have both, switch with **Job Management** / **Certifications** in the header.
- The office bar is grouped into **Time**, **Per diem**, **Pay setup**, **People** and **Jobs** dropdowns, with **Problems** on its own.
- Supervisors and technicians can see their own certifications (**Mine**). Supervisors can also see their crew's (**My crew**).
- On **New crew day**, once the job, date and people are picked, **Certifications for this job:** lists anyone missing or expired on a card the job needs. It is a warning only. It never stops the day. (Certifications accounts only.)
- Expiry reminder emails are built but switched off until NW Linings decides who gets them.
- Wide tables now always show a scroll bar, on Windows and on a Mac.

## October 11, 2026 (from Tim's review)

- **Move a crew day.** **Wrong job or date?** on a crew day moves it, with its per diem and location reading. See [Move a crew day](/help/move-a-crew-day).
- **Untick per diem** on **My crew**. See [My crew](/help/my-crew).
- Supervisors can no longer change a per diem amount. It always comes from the rate table.
- Every change a supervisor makes to recorded time asks for a note. A word is enough.
- No location reading? You are asked for a reason when you submit. It never blocks the day.
- The office can move, clear or hand-place a job's map pin. See [Job locations](/help/job-locations).
- Fixed: **My crew** now offers the current week.
- Fixed: **My time → Review the week** no longer says "Requires Office role".

## August 29, 2026

- **I'm on site**: record where you are when you start a crew day. See [Record a crew day](/help/crew-day).
- **Job locations** for the office, to pin each job from those readings.
- Supervisors can **correct hours** on **My crew** by tapping a day total.
- People added from outside your crew show in the Crew card under **Added for today**.
- **Per diem only** offers **Last week** and **This week**, so Sunday still reaches Saturday.

## August 25, 2026

- Record people who are on nobody's crew. If someone is already recorded elsewhere that day, you see a warning.
- Record a new hire before the office has them in Sage. The office links them later.
- End-of-day travel can be charged to the job the crew drove to.
- Refreshing the page in the middle of a crew day puts you back where you were.
- The per diem button counts per diems, not days, with the sum shown above it.`,
  },

  // -------------------------------------------------------------- glossary
  {
    slug: 'glossary',
    title: 'Glossary',
    section: 'Reference',
    audience: 'all',
    summary: 'Plain-language meanings of the words used across the portal.',
    body: `## Time and pay

| Term | What it means |
| --- | --- |
| **Crew day** | One job, one date, one crew, with everyone's hours. The supervisor records it on **New crew day**. |
| **Submission** | The office's word for a crew day once it has been sent. **Time → Submissions** lists them. |
| **Work type** | The plain name for a kind of time, like Labor, Travel, Standby or Training. Each one carries the Sage codes behind it, so the field never has to pick a code. |
| **Earnings code** | Sage's code for a kind of pay (Regular, Overtime, Per diem and so on). The office sees these. The field does not. |
| **Straight time** | Every field hour is entered at the regular rate. The office applies overtime afterward. |
| **Prevailing wage** | Public work where the pay rate is set for the job and the type of work done, not by the person's usual rate. The rates live on **Pay setup → Wage rates**. |
| **Davis-Bacon** | Federal prevailing wage rules. A job flag in Sage. |
| **Out of trade** | Part of a day spent doing a different trade's work on a prevailing wage job, paid at that trade's rate. |
| **Labor register** | The office's grid of recorded time, laid out like Sage Time Track Entry. |
| **Pay period / week ending** | A pay week runs Sunday to Saturday and is named by its Saturday, the "week ending" date. |
| **Sent / posted to Sage** | A line that has gone into Sage. It can no longer be changed in the portal. Fix it in Sage. |
| **Paid in Sage** | A week Sage has already run payroll for. Every day in it is locked in the portal. |

## Per diem

| Term | What it means |
| --- | --- |
| **Per diem** | A daily amount for staying away from home for a job. One per person per day at most. |
| **Per diem only** | Per diem for a day nobody worked, like a weekend at the job. |
| **Per diem site** | A special per diem location for a job that pays differently from its state, like Dutch Harbor. Set on **Jobs → Jobs**. |
| **Crew roles: Super / QC / Technician** | The three per diem rates. Super is anyone with the Supervisor role. QC is a technician with the Quality classification. Everyone else in the field is Technician. |

## People

| Term | What it means |
| --- | --- |
| **Crew** | A supervisor and the technicians who usually work for them. Set by the office on **People → Crews**. |
| **Floater** | A technician on nobody's crew. Any supervisor can record their day. |
| **New hire (provisional person)** | Someone a supervisor added in the field before they are in Sage. They get a temporary number starting with P. |
| **Linked employee** | A new hire the office has matched to their real Sage record on **People → New people**. Their hours move to that record and can then go to Sage. |
| **Already recorded elsewhere** | That person already has hours on that date on another crew day. A warning, not a refusal: a split day is real. |

## Jobs and location

| Term | What it means |
| --- | --- |
| **Travel / destination** | Travel is its own work type. End-of-day travel can be charged to the job the crew drove to, the destination, instead of the job they left. |
| **Check-in / site reading** | The location your phone records when you tap **I'm on site**. It records where the phone was. It does not prove anything on its own. |
| **Pin** | Where a job is on the map, set by the office on **Jobs → Job locations**. Readings are compared to it: **On site**, **Away from site**, **Cannot tell** or **No site pinned**. |
| **Problems** | The office's list of things that will cause trouble at payroll time, with a count on the button. |

## Certifications

| Term | What it means |
| --- | --- |
| **Certification (card)** | A training or qualification someone holds, like OSHA 10 or a forklift evaluation, with its issue and expiry dates. |
| **Requirement** | A rule that says who needs a certification: everyone, or people on a certain kind of job, customer or job. |
| **Equivalent / counts as** | Holding one card meets the requirement for another. OSHA 30 counts as OSHA 10. Shows as **Met by an equivalent**. |
| **Prerequisite / requires** | One card is only good if another was in place first. A fit test requires medical clearance. If not, it shows **Blocked by a prerequisite**. |
| **Verified / awaiting verification** | Verified means the office has seen the card. **Awaiting verification** means it has been entered but not checked yet. |
| **Expiring soon** | Still current, but close to its expiry date. |
| **Grace period** | The number of days a new hire has before a missing card counts against them. Shows as **New hire — in grace period**. |
| **Re-evaluation required** | An Admin has flagged that a card needs to be redone, for example after a medical change. |
| **Restricted (medical)** | Medical cards. Only an Admin sees their details. Everyone else sees only whether the person is OK. |`,
  },

  // ------------------------------------------------------------------- FAQ
  // TODO(verify): "the office can't change a travel destination yet" is from
  // PROJECT_STATE §5 (known gaps); drop that answer when it is fixed.
  {
    slug: 'faq',
    title: 'FAQ',
    section: 'Reference',
    audience: 'all',
    summary: 'Short answers to the questions people ask most.',
    body: `## Crew days

### I picked the wrong job or date. What do I do?

Open the crew day and use **Wrong job or date?**. Tap **Check the move** to see what will change, then move it. Hours, per diem and the location reading move together. See [Move a crew day](/help/move-a-crew-day).

### Why can't I change a day?

It is locked. Either a line has already gone to Sage, or Sage has already paid that week. Ask the office. They fix it in Sage.

### Why does someone show as "Already recorded elsewhere on this date"?

Another crew day already has hours for that person on that date. It is a warning, not a block. If they really split the day between two jobs, carry on. If not, check with the other supervisor before you send, so they are not paid twice.

### What if I have no signal?

Keep going. Everything you type is saved on the phone. When you have signal, open the crew day and tap **Submit crew day** again. It does not send by itself. See [Working without signal](/help/working-offline).

### Do I have to tap "I'm on site"?

No, but if you skip it you are asked why when you submit. Any reason works, like "no signal in the tank". It never stops the day.

### Can I record someone who is not on my crew?

Yes, if they are on nobody's crew, or a new hire the office has not set up yet. Use **+ Add someone not on your crew**. You cannot record another supervisor's crew. See [Record a crew day](/help/crew-day).

### Why does it ask me for a note?

Every change a supervisor makes to recorded time needs a note. One word is fine, like "typo".

### Where do I put overtime?

You don't. Enter every hour at straight time. The office applies overtime.

## Per diem

### Why can't I change the per diem amount?

The amount comes from the rate table, based on the job's location and the person's rate (Super, QC or Technician). Only the office can change it.

### The crew stayed over the weekend but did not work. How do I pay per diem?

Use **Per diem only** on **Today**. See [Per diem only](/help/per-diem-only).

### Why was a per diem refused?

A person can have only one per diem a day. If they already have one that day, a second is refused.

### I ticked per diem by mistake.

Untick it on **My crew**, with a note. See [My crew](/help/my-crew).

## Office

### Has any time gone into Sage yet?

Not yet. Sending time into Sage payroll is not switched on. See [Sending time to Sage](/help/sending-time-to-sage).

### Why can't a new hire's hours go to Sage?

They are not linked to a Sage record yet. Link them on **People → New people** once they are in Sage.

### A supervisor picked the wrong travel destination. Can I change it?

Not from the office screens yet. Use **Send feedback** with the crew day, and it will be sorted out.

## Certifications

### Why can't I see Certifications?

It is switched on for some accounts while it is tested. You will see **Certifications aren't switched on for your account.** until it is opened up.

### Will a missing certification stop a crew day?

No. **Certifications for this job:** on **New crew day** is a warning only.

### Why can't I see the dates on a medical card?

Medical cards are restricted. Only an Admin sees their details. Everyone else sees whether the person is OK.

## Account

### I forgot my PIN.

Ask the office. They set a new one. A PIN cannot be looked up.

### It says my account is locked.

Too many wrong tries. Wait about 15 minutes, or ask the office to set a new PIN, which unlocks you straight away.`,
  },

  // -------------------------------------------------------- troubleshooting
  // TODO(verify): the "Saturday evening shows next week" item is a known gap
  // (PROJECT_STATE §5, server uses UTC). Remove it when fixed.
  {
    slug: 'troubleshooting',
    title: 'Troubleshooting',
    section: 'Reference',
    audience: 'all',
    summary: 'Something looks wrong? Find what you see, then what to do.',
    body: `Find what you are seeing, then try the steps. If none of them help, use **Send feedback** at the bottom of the help panel.

## The page looks out of date, or a button is missing

The portal is updated often, and your browser may be showing an older copy.

- **Mac:** press **Cmd-Shift-R**.
- **Windows:** press **Ctrl-Shift-R**.
- **Phone:** pull down on the page to refresh. If that does not help, close the browser tab or home screen icon fully and open it again.

Refreshing in the middle of a crew day is safe. You come back to the same step with everything you entered.

## No signal, or a crew day is waiting to send

- The yellow box **Showing saved job and crew lists — could not reach the server.** means the phone is offline. You can keep entering.
- **Could not send. It is saved on this phone** means the day did not go. Find signal, open **Today**, tap **Continue crew day**, then **Submit crew day** again.
- It does not send by itself. You are done only when **Today** shows **Sent — ...**.
- Sending twice is safe. If the first one got through, you see **That crew day was already recorded.**

See [Working without signal](/help/working-offline).

## "Not available for your role."

The screen is for a different role. Supervisors use the crew time screens, the office uses the office screens, and technicians only have Certifications. If you think your role is wrong, ask the office. A message like **Requires Office role or higher** means the same thing.

## "Certifications aren't switched on for your account."

Certifications is being tested with a few people. It will open up later.

## Can't sign in

- Check you are on the right tab: **Field** for employee number and PIN, **Office** for email and password.
- Employee numbers look like F1505.
- **Account locked. Try again shortly.**: wait about 15 minutes, or ask the office for a new PIN.
- **This account signs in with your Microsoft account.**: ask the office how to sign in.
- If you get errors after a long break, tap **Sign out** and sign in again.

See [Signing in](/help/signing-in).

## The portal refused something

Read the red message. It says why. The common ones:

- **A note is required when changing recorded time**: type a note, even just "typo".
- The day is locked because it went to Sage or Sage paid that week. The office fixes it in Sage.
- A second per diem on the same day for the same person.
- A move to a job that cannot price someone's per diem.

If the reason does not make sense, send feedback with what you were trying to do.

## Next week shows up early on Saturday evening

From late Saturday afternoon, Pacific time, lists of pay weeks can show the next week a few hours early. Nothing is wrong with your time. Pick the week you want. This will be fixed.

## Reporting a problem

Use **Send feedback** at the bottom of the help panel. Pick bug, question or idea, and say what you were doing and what you expected. It records the page you were on, so you do not need to explain where you were.`,
  },
]
